<template>
  <AccountingImportDialog
    v-model:visibility="accoutingImportDialogVisible"
    :reanalyze-import-id="reanalyzeImportId"
    @imported="handleAccountingImportSuccess"
    @closed="handleDialogClosed" />

  <main class="datasource flex flex-col gap-6 !border-t-0 p-0">
    <el-tabs v-model="activeTab" class="h-full overflow-hidden">
      <!-- Overview -->
      <el-tab-pane
        name="overview"
        :label="i18n.t('overview')"
        class="flex flex-col">
        <div class="grid-wrapper !mt-0">
          <el-card body-class="flex flex-col h-full items-start">
            <h3 class="mb-2">{{ i18n.t('hrTools') }}</h3>
            <p class="mb-4">
              {{ i18n.t('hrToolDescription') }}
            </p>
            <SmButton class="mt-auto" @click="activeTab = 'hr'">
              {{ i18n.t('view') }}
            </SmButton>
          </el-card>
          <el-card body-class="flex flex-col h-full items-start">
            <h3 class="mb-2">{{ i18n.t('ssoProvider') }}</h3>
            <p class="mb-4">
              {{ i18n.t('ssoProviderDescription') }}
            </p>
            <SmButton class="mt-auto" @click="activeTab = 'sso'">
              {{ i18n.t('view') }}
            </SmButton>
          </el-card>
          <el-card body-class="flex flex-col h-full items-start">
            <h3 class="mb-2">{{ i18n.t('accountingImports') }}</h3>
            <p class="mb-4">
              {{ i18n.t('datevImportDescription') }}
            </p>
            <SmButton class="mt-auto" @click="activeTab = 'imports'">
              {{ i18n.t('view') }}
            </SmButton>
          </el-card>
          <el-card
            v-if="browserExtensionEnabled"
            body-class="flex flex-col h-full items-start ">
            <div class="mb-2 flex w-full items-center justify-between gap-4">
              <h3>
                {{ i18n.t('browserExtension.header') }}
              </h3>
            </div>

            <p class="mb-4">
              {{ i18n.t('browserextensionDescription') }}
            </p>
            <SmButton class="mt-auto" @click="activeTab = 'browserextension'">
              {{ i18n.t('view') }}
            </SmButton>
          </el-card>
        </div>
      </el-tab-pane>

      <!-- HR Tools Tab -->
      <!-- todo: refactor and seperate into a indivdual component -->
      <el-tab-pane name="hr" :label="i18n.t('hrTools')" class="flex flex-col">
        <h2 class="mb-2">{{ i18n.t('hrTools') }}</h2>
        <p>
          {{ i18n.t('hrToolDescription') }}
        </p>

        <div class="grid-wrapper">
          <template v-if="loading">
            <SmSkeleton
              v-for="i in 12"
              :key="i"
              height="200px"
              width="100%"
              class="!rounded-lg !bg-gray-100 dark:!bg-gray-800" />
          </template>
          <DatasourceCard
            v-for="dataSource in dataSourcesSorted"
            v-else
            :key="dataSource.integration_id"
            :loading="loading"
            :datasource="dataSource"
            :connected="isDataSourceConnected(dataSource)"
            :connection="getDataSourceConnection(dataSource)" />
        </div>
      </el-tab-pane>

      <!-- SSO -->
      <!-- todo: refactor and seperate into a indivdual component -->
      <el-tab-pane
        name="sso"
        :label="i18n.t('ssoProvider')"
        class="flex h-full flex-col"
        lazy>
        <div class="datasource-header">
          <h2 class="mb-2">{{ i18n.t('ssoProvider') }}</h2>
          <p>
            {{ i18n.t('ssoProviderDescription') }}
          </p>
        </div>

        <div class="grid-wrapper datev">
          <template v-if="loading">
            <SmSkeleton
              v-for="i in 3"
              :key="i"
              height="200px"
              width="100%"
              class="mb-4 !rounded-lg !bg-gray-100 dark:!bg-gray-800" />
          </template>
          <DatasourceCard
            v-for="ssoProvider in availableSSOProviders"
            v-else
            :key="ssoProvider.name"
            :loading="loading"
            :datasource="ssoProvider"
            :connected="connectedSSOProvidersNames.includes(ssoProvider.name)"
            :connection="getSSOConnection(ssoProvider)" />
        </div>
      </el-tab-pane>

      <el-tab-pane
        name="imports"
        :label="i18n.t('accountingImports')"
        class="flex h-full flex-col"
        lazy>
        <div class="flex h-full flex-col gap-4">
          <el-card
            shadow="always"
            body-class="flex flex-col"
            class="flex flex-1"
            :body-style="{ height: '100%', width: '100%' }">
            <AccountingImportsTable
              ref="accountingImportsTableRef"
              @upload="accoutingImportDialogVisible = true"
              @reanalyze="handleReanalyze" />
          </el-card>
        </div>
      </el-tab-pane>

      <!-- Browser Extension -->
      <el-tab-pane
        v-if="browserExtensionEnabled"
        name="browserextension"
        :label="i18n.t('browserExtension.header')"
        class="flex h-full flex-col"
        lazy>
        <el-card
          shadow="always"
          body-class="flex flex-col"
          class="h-full"
          :body-style="{
            height: '100%',
            width: '100%',
          }">
          <BrowserExtensionTabs />
        </el-card>
      </el-tab-pane>
    </el-tabs>
  </main>
</template>

<script setup lang="ts">
  import { useDocumentVisibility } from '@vueuse/core'
  import { useI18n } from 'vue-i18n'
  import {
    DataSource,
    DataSourceInformation,
    DataSourceIntegrationsService,
    DataSourcesService,
    Employee,
    EmployeesService,
    SoftwareInformation,
    SoftwareIntegrationsService,
    SoftwareOut,
  } from '@/client'
  import { ApplicationStore } from '@/stores/application/application.store'
  import BrowserExtensionTabs from '@/components/browserextension/BrowserExtensionTabs.vue'
  import AccountingImportsTable from '@/components/accounting/AccountingImportsTable.vue'
  import { useSessionStore } from '@/stores/sessionStore'
  import { useRoute, useRouter } from 'vue-router'

  const i18n = useI18n()
  const route = useRoute()
  const router = useRouter()

  const employees = ref<null | Employee[]>(null)

  const store = useSessionStore()

  const browserExtensionEnabled = computed(() => {
    return !!store.currentCompanyInfo?.features.browser_extension
  })
  // ----------------------------------------------------------------------
  // Tabs
  // ----------------------------------------------------------------------

  const validTabs = ['overview', 'hr', 'sso', 'imports', 'browserextension']
  const activeTab = ref(
    validTabs.includes((route.query.tab as string) ?? '')
      ? (route.query.tab as string)
      : 'overview'
  )
  const showFade = computed(() => {
    return activeTab.value !== 'browserextension' ? 'block' : 'none'
  })

  // ##############################
  // #  Data Sources
  // ##############################

  const accoutingImportDialogVisible = ref(false)
  const reanalyzeImportId = ref<string | null>(null)
  const accountingImportsTableRef =
    ref<InstanceType<typeof AccountingImportsTable>>()

  function handleAccountingImportSuccess() {
    accountingImportsTableRef.value?.reload()
    reanalyzeImportId.value = null
  }

  function handleDialogClosed() {
    accountingImportsTableRef.value?.reload()
    reanalyzeImportId.value = null
  }

  function handleReanalyze(importRecord: { _id: string }) {
    reanalyzeImportId.value = importRecord._id
    accoutingImportDialogVisible.value = true
  }

  const dataSources = ref<null | DataSourceInformation[]>(null)
  const connectedDataSources = ref<null | DataSource[]>(null)

  onMounted(async () => {
    getAvailableDataSources()
    reloadConnectedDataSources()
    loadEmployees()
  })

  const connectedDataSourcesIntegrationIds = computed(() => {
    return connectedDataSources.value?.map(
      (dataSource) => dataSource.integration_id
    )
  })

  watch(
    () => route.query.tab,
    (tab) => {
      if (typeof tab === 'string' && validTabs.includes(tab)) {
        activeTab.value = tab
      }
    }
  )

  watch(
    activeTab,
    (nextTab) => {
      const query = { ...route.query }
      if (nextTab === 'overview') {
        delete query.tab
      } else {
        query.tab = nextTab
      }
      router.replace({ name: 'Discovery', query })
    },
    { flush: 'post' }
  )

  function getAvailableDataSources() {
    // Get all available data sources
    DataSourceIntegrationsService.getAvailableDataSourcesApiV1DataSourceAvailableGet(
      {
        excludeConnected: false,
      }
    ).then((res) => {
      dataSources.value = res
    })
  }

  // When return to this page, refresh the data sources
  const visibility = useDocumentVisibility()
  watch(visibility, async (current, previous) => {
    if (current === 'visible' && previous === 'hidden') {
      reloadConnectedDataSources()
      getAvailableSSOProviders()
      getConnectedSSOProviders()
    }
  })

  const dataSourcesSorted = computed(() => {
    // First sort by connected and then by name
    const conntected: Array<DataSourceInformation> = []
    const notConnected: Array<DataSourceInformation> = []

    dataSources.value?.forEach((dataSource) => {
      if (isDataSourceConnected(dataSource)) {
        conntected.push(dataSource)
      } else {
        notConnected.push(dataSource)
      }
    })

    // Sort by name
    conntected.sort((a, b) => {
      return a.name.localeCompare(b.name)
    })

    notConnected.sort((a, b) => {
      return a.name.localeCompare(b.name)
    })

    return [...conntected, ...notConnected]
  })

  function reloadConnectedDataSources() {
    // Get all connected data sources
    DataSourcesService.getDataSourcesApiV1DataSourceDataSourcesGet({}).then(
      (res) => {
        connectedDataSources.value = res
      }
    )
  }

  function isDataSourceConnected(dataSource: DataSourceInformation) {
    return (
      connectedDataSourcesIntegrationIds.value?.includes(
        dataSource.integration_id
      ) || false
    )
  }

  function getDataSourceConnection(dataSource: DataSourceInformation) {
    return connectedDataSources.value?.find(
      (d) => d.integration_id === dataSource.integration_id
    )
  }

  // ##############################
  // #  SSO Providers
  // ##############################
  const loading = ref(false)
  const connectedSSOProviders = ref<SoftwareOut[]>([])
  const availableSSOProviders = ref<SoftwareInformation[]>([])

  onMounted(async () => {
    loading.value = true
    await getAvailableSSOProviders()
    await getConnectedSSOProviders()

    loading.value = false
  })

  function getSSOConnection(ssoProvider: SoftwareInformation) {
    return connectedSSOProviders.value.find(
      (s) => s.software_name === ssoProvider.name
    )
  }
  const connectedSSOProvidersNames = computed(() => {
    return connectedSSOProviders.value.map((s) => s.software_name)
  })

  async function getConnectedSSOProviders() {
    await ApplicationStore.getApplications<SoftwareOut>().then((res) => {
      connectedSSOProviders.value = (res as SoftwareOut[]).filter(
        (s) => s.information.sso_provider
      )
    })
  }

  async function getAvailableSSOProviders() {
    await SoftwareIntegrationsService.getIntegrationsApiV1SoftwareIntegrationsGet(
      {}
    ).then((res) => {
      availableSSOProviders.value = res.filter((s) => s.sso_provider)
    })
  }

  function loadEmployees() {
    EmployeesService.getEmployeesApiV1DataSourceEmployeesGet({}).then((res) => {
      employees.value = res
    })
  }
</script>

<style lang="scss" scoped>
  .background-gradient {
    background: linear-gradient(
      20deg,
      var(--sm-primary-80) 0%,
      transparent 60%
    );
  }

  .grid-wrapper {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    grid-gap: 20px;
    margin-top: 20px;
    padding: 0;
    margin-bottom: 3rem;
  }

  .sm-card {
    background-color: var(--sm-elevation);
    border-radius: var(--border-radius);
    box-shadow: var(--shadow);
    padding: 16px;
    margin-bottom: 0;
  }

  .data-source-card {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .data-source-media {
    display: flex;
    justify-content: center;
    align-items: center;

    border-radius: 50%;

    width: 43px;
    height: 43px;

    padding: 7px;

    box-shadow: var(--shadow);

    & > img {
      display: block;
      max-width: 28px;
      max-height: 28px;
      width: auto;
      height: auto;

      border-radius: 10%;
    }
  }

  .data-source-header {
    display: flex;
    gap: 5px;

    justify-content: space-between;
    align-items: center;
  }

  p {
    font-size: 14px;
    color: var(--sm-contrast-muted);
    margin-top: 0;
  }

  .datasource {
    display: flex;
    flex-direction: column;

    .datasource-content {
      overflow-y: auto;
    }

    .fade-out {
      position: absolute;
      width: 100%;
      height: 1.5rem;

      background: linear-gradient(
        0deg,
        var(--sm-background) 0%,
        rgba(255, 255, 255, 0) 100%
      );
      bottom: 20px;
    }
  }

  // Prevent the shadow from being cut off
  .home {
    padding-left: 0;
    padding-right: 0;
    overflow: hidden;

    > div {
      padding-left: var(--home-padding);
      padding-right: var(--home-padding);
    }
  }
</style>

<style lang="scss">
  .datasource .el-tabs__item {
    padding: 0 12px;
  }

  .datasource .el-tabs__header {
    margin: 0;
  }

  .datasource .el-tabs__content {
    overflow-x: visible;
    overflow-y: auto;
    padding: var(--home-padding);
  }

  .datasource .el-tabs__nav-wrap {
    padding: 0 var(--home-padding);
  }

  .datasource .el-tabs {
    position: relative;
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      margin: 0 var(--home-padding);
      height: 50px;
      background: linear-gradient(to top, var(--sm-background), transparent);
      pointer-events: none;
      display: v-bind(showFade);
    }
  }
</style>
